import { cn } from "@faire/prototype-playground/lib/utils";
import { Button } from "@faire/slate/components/button";
import React from "react";

import { PADX } from "../constants";
import { type PriceMode } from "../PriceLabel";
import { ProductCard, type ProductCardData } from "../ProductCard";
import { SectionHeader } from "../SectionHeader";

interface BestsellersRowProps {
  products: ProductCardData[];
  priceMode: PriceMode;
  title?: string;
  /**
   * When set, each card links to `linkFor(product)` (brand.com → Faire) instead
   * of opening the sign-up gate.
   */
  linkFor?: (product: ProductCardData) => string;
  /** Passed through to the section header — hide the carousel arrows and
   * relabel / route the "see all" action. */
  showArrows?: boolean;
  seeAllLabel?: string;
  seeAllVariant?: "link" | "button";
  /** Where the "see all" CTA renders. "header" (default) is a small link
   * next to the title; "bottom" is a prominent, centered button below the
   * grid instead. */
  seeAllPlacement?: "header" | "bottom";
  onSeeAll?: () => void;
  /** 1 (default) = horizontal-scroll carousel; 2+ = a fixed grid of rows ×
   * `columns` products, no scroll. */
  rows?: number;
  /** Desktop column count for the `rows >= 2` grid — 6 (default) matches
   * every other product grid; 5 reads as a deliberately distinct layout
   * (e.g. a "Top Sellers" row meant to stand out from marketplace rows). */
  columns?: 5 | 6;
  /** See `BrandDataset.productImageAspect`. */
  aspectRatio?: "square" | "portrait";
  /**
   * Show every Nth tile as its lifestyle photo instead of its packshot, so the
   * row reads as mixed content rather than one long wall of the same angle on
   * the same background. 3 gives roughly one in three. Deterministic by index
   * on purpose — a random mix would reshuffle on every render and make visual
   * diffs useless. Off when undefined.
   */
  lifestyleEvery?: number;
  /** Drop names/prices under the tiles — showcase surfaces only. */
  hideDetails?: boolean;
}

export const BestsellersRow: React.FC<BestsellersRowProps> = ({
  products,
  priceMode,
  title = "Bestsellers",
  linkFor,
  showArrows,
  seeAllLabel,
  seeAllVariant,
  seeAllPlacement = "header",
  onSeeAll,
  rows = 1,
  columns = 6,
  aspectRatio,
  lifestyleEvery,
  hideDetails,
}) => {
  if (products.length === 0) return null;
  const showBottomCta = seeAllPlacement === "bottom" && onSeeAll;
  // Offset by 1 so the row doesn't open on a lifestyle shot — the first tile
  // still reads as "product", and the variety arrives as you scan across.
  const isLifestyleTile = (index: number) =>
    Boolean(lifestyleEvery) && index % (lifestyleEvery as number) === 1;
  return (
    <div className={cn("pt-8 pb-4", PADX)}>
      <SectionHeader
        title={title}
        showArrows={showArrows}
        showSeeAll={!showBottomCta}
        seeAllLabel={seeAllLabel}
        seeAllVariant={seeAllVariant}
        onSeeAll={onSeeAll}
      />
      {rows >= 2 ? (
        <div
          className={cn(
            "grid grid-cols-2 gap-x-[var(--grid-gutter,16px)] gap-y-6 md:grid-cols-3",
            columns === 5 ? "lg:grid-cols-5" : "lg:grid-cols-6"
          )}
        >
          {products.slice(0, rows * columns).map((product, index) => (
            <div key={product.token} className="min-w-0">
              <ProductCard
                product={product}
                priceMode={priceMode}
                href={linkFor?.(product)}
                aspectRatio={aspectRatio}
                showLifestyleImage={isLifestyleTile(index)}
                hideDetails={hideDetails}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {products.map((product, index) => (
            <div
              key={product.token}
              className="w-[42%] flex-shrink-0 sm:w-[30%] lg:w-[calc((100%_-_80px)/6)]"
            >
              <ProductCard
                product={product}
                priceMode={priceMode}
                href={linkFor?.(product)}
                aspectRatio={aspectRatio}
                showLifestyleImage={isLifestyleTile(index)}
                hideDetails={hideDetails}
              />
            </div>
          ))}
        </div>
      )}
      {showBottomCta ? (
        <div className="mt-6 flex justify-center">
          <Button variant="secondary" onClick={onSeeAll}>
            {seeAllLabel ?? "Shop all products"}
          </Button>
        </div>
      ) : null}
    </div>
  );
};
